import { useEffect, useState } from "react";
import { FileSpreadsheet, Images, Package, Pencil, Plus, Search, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, fmtTL, API, QUALITY_OPTIONS } from "../../lib/api";
import QualityBadge from "../QualityBadge";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import CriticalPinDialog from "../CriticalPinDialog";
import SmartCategoryPicker from "../SmartCategoryPicker";

const catLabel = (cats, r) => {
    if (!r.category_id) return r.kategori;
    const c = cats.find((x) => x.category_id === r.category_id);
    if (!c) return r.kategori;
    const p = c.parent_id ? cats.find((x) => x.category_id === c.parent_id) : null;
    return p ? `${p.name} › ${c.name}` : c.name;
};

export default function ProductsTab() {
    const [products, setProducts] = useState(null);
    const [cats, setCats] = useState([]);
    const [q, setQ] = useState("");
    const [editor, setEditor] = useState(null);
    const [pinTarget, setPinTarget] = useState(null);
    const [busy, setBusy] = useState(false);
    const [exOpen, setExOpen] = useState(false);
    const [exPreview, setExPreview] = useState(null);
    const [exBusy, setExBusy] = useState(false);
    const [catFilter, setCatFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [prodSort, setProdSort] = useState("name");
    const [mediaOpen, setMediaOpen] = useState(false);
    const [mediaItems, setMediaItems] = useState(null);
    const [mediaQ, setMediaQ] = useState("");
    const [imgBusy, setImgBusy] = useState(false);
    const [sugMedia, setSugMedia] = useState([]);

    useEffect(() => {
        const name = (editor?.name || "").trim();
        if (!editor || name.length < 3) { setSugMedia([]); return; }
        const t = setTimeout(async () => {
            let items = [];
            try {
                const r = await api.get(`/media?limit=6&q=${encodeURIComponent(name)}`);
                items = r.data.items;
                if (!items.length && name.includes(" ")) {
                    const r2 = await api.get(`/media?limit=6&q=${encodeURIComponent(name.split(" ")[0])}`);
                    items = r2.data.items;
                }
            } catch { /* öneri yok */ }
            setSugMedia(items);
        }, 400);
        return () => clearTimeout(t);
    }, [editor?.name, editor?.product_id]);

    const fetchMedia = async (query = "") => {
        try {
            const r = await api.get(`/media?limit=48${query ? `&q=${encodeURIComponent(query)}` : ""}`);
            setMediaItems(r.data.items);
        } catch {
            setMediaItems([]);
        }
    };

    const openMediaPicker = () => {
        setMediaOpen(!mediaOpen);
        if (!mediaItems) fetchMedia();
    };

    const uploadEditorImage = async (file) => {
        if (!file) return;
        setImgBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/media/upload", fd);
            setEditor((e) => ({ ...e, image: r.data.url }));
            toast.success("Görsel kütüphaneye yüklendi ve ürüne atandı");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Yüklenemedi");
        } finally {
            setImgBusy(false);
        }
    };

    const uploadExcel = async (file) => {
        if (!file) return;
        setExBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/excel/import?module=products", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setExPreview(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Excel okunamadı");
        } finally {
            setExBusy(false);
        }
    };

    const commitExcel = async () => {
        setExBusy(true);
        try {
            const r = await api.post(`/excel/import/${exPreview.import_id}/commit`);
            toast.success(`${r.data.applied} ürün kataloğa eklendi (${exPreview.import_id})`);
            setExOpen(false);
            setExPreview(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Uygulanamadı");
        } finally {
            setExBusy(false);
        }
    };

    const load = () => {
        api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
        api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!editor.product_id && (!editor.name?.trim() || !editor.category_id)) {
            toast.error("Ürün adı ve kategori zorunludur");
            return;
        }
        setBusy(true);
        try {
            if (editor.product_id) {
                await api.patch(`/admin/products/${editor.product_id}`, {
                    name: editor.name, category_id: editor.category_id, unit: editor.unit,
                    description: editor.description, status: editor.status, image: editor.image, quality: editor.quality || "",
                });
                toast.success("Ürün güncellendi");
            } else {
                await api.post("/admin/products", {
                    name: editor.name, category_id: editor.category_id, unit: editor.unit,
                    description: editor.description, image: editor.image, quality: editor.quality || "",
                });
                toast.success("Ürün kataloğa eklendi");
            }
            setEditor(null);
            setMediaOpen(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kaydedilemedi");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (pin) => {
        setBusy(true);
        try {
            await api.delete(`/admin/products/${pinTarget.product_id}`, { headers: { "X-Critical-Pin": pin } });
            toast.success("Ürün kalıcı olarak silindi");
            setPinTarget(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        } finally {
            setBusy(false);
        }
    };

    const filtered = (products || [])
        .filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.sku || "").toLowerCase().includes(q.toLowerCase()))
        .filter((p) => catFilter === "all" || p.category_id === catFilter)
        .filter((p) => statusFilter === "all" || p.status === statusFilter)
        .sort((a, b) =>
            prodSort === "name" ? a.name.localeCompare(b.name, "tr")
            : prodSort === "name_desc" ? b.name.localeCompare(a.name, "tr")
            : prodSort === "offers" ? (b.offers?.length || 0) - (a.offers?.length || 0)
            : (a.sku || "").localeCompare(b.sku || "", "tr")
        );

    return (
        <div data-testid="admin-products-tab">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün adı veya SKU ara..." className="h-9 w-64 pl-8 text-sm" data-testid="admin-product-search" />
                </div>
                <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="h-9 rounded-md border bg-background px-2 text-xs" data-testid="admin-product-cat-filter">
                    <option value="all">Tüm Kategoriler</option>
                    {cats.map((c) => <option key={c.category_id} value={c.category_id}>{c.parent_id ? "— " : ""}{c.name}</option>)}
                </select>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 rounded-md border bg-background px-2 text-xs" data-testid="admin-product-status-filter">
                    <option value="all">Durum: Tümü</option>
                    <option value="active">Aktif</option>
                    <option value="passive">Pasif</option>
                </select>
                <select value={prodSort} onChange={(e) => setProdSort(e.target.value)} className="h-9 rounded-md border bg-background px-2 text-xs" data-testid="admin-product-sort">
                    <option value="name">Ad (A-Z)</option>
                    <option value="name_desc">Ad (Z-A)</option>
                    <option value="sku">SKU</option>
                    <option value="offers">Teklif Sayısı</option>
                </select>
                <div className="flex items-center gap-2">
                    <Button size="sm" className="bg-terra-600 text-white hover:bg-terra-700" onClick={() => { setEditor({ name: "", category_id: "", unit: "kg", status: "active", description: "", image: "", quality: "" }); setMediaOpen(false); setMediaItems(null); }} data-testid="admin-product-add-button">
                        <Plus className="mr-1.5 h-4 w-4" /> Ürün Ekle
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setExOpen(true); setExPreview(null); }} data-testid="admin-product-excel-button">
                        <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel ile Toplu Ekle
                    </Button>
                    <span className="text-xs text-muted-foreground">{filtered.length} ürün</span>
                </div>
            </div>
            {!products ? (
                <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded-2xl bg-muted" />)}</div>
            ) : (
                <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
                    <div className="max-h-[520px] overflow-x-auto overflow-y-auto">
                        <table className="w-full text-sm" data-testid="admin-products-table">
                            <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                                <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                                    <th className="p-3">Ürün</th><th className="p-3">Kategori</th><th className="p-3">Birim</th><th className="p-3">Teklifler</th><th className="p-3">Durum</th><th className="p-3 text-right">İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((p) => (
                                    <tr key={p.product_id} className={`border-b last:border-0 hover:bg-muted/40 ${p.status !== "active" ? "opacity-50" : ""}`} data-testid={`admin-product-row-${p.product_id}`}>
                                        <td className="p-3">
                                            <div className="flex items-center gap-2.5">
                                                {p.image ? (
                                                    <img src={p.image} alt="" className="h-9 w-9 shrink-0 rounded-lg border object-cover" data-testid={`admin-product-img-${p.product_id}`} />
                                                ) : (
                                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted"><Images className="h-4 w-4 text-muted-foreground" /></div>
                                                )}
                                                <div>
                                                    <p className="font-semibold">{p.name}</p>
                                                    <p className="text-xs text-muted-foreground font-mono">{p.sku}</p>
                                                    <QualityBadge quality={p.quality} tid={p.product_id} />
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-3 text-muted-foreground">{p.category_name}</td>
                                        <td className="p-3">{p.unit}</td>
                                        <td className="p-3">
                                            {p.offers.map((o) => (
                                                <p key={o.offer_id} className="text-xs text-muted-foreground">{fmtTL(o.price)} • {o.stock} stok</p>
                                            ))}
                                            {!p.offers.length && <span className="text-xs text-muted-foreground">—</span>}
                                        </td>
                                        <td className="p-3">
                                            <Badge variant="outline" className={`text-[10px] ${p.status === "active" ? "border-emerald-200 bg-emerald-100 text-emerald-800" : "border-red-200 bg-red-100 text-red-700"}`}>
                                                {p.status === "active" ? "Aktif" : "Pasif"}
                                            </Badge>
                                        </td>
                                        <td className="p-3 text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setEditor({ ...p })} data-testid={`admin-product-edit-${p.product_id}`}><Pencil className="mr-1 h-3 w-3" /> Düzenle</Button>
                                                <Button size="sm" variant="outline" className="h-8 text-xs text-destructive" onClick={() => setPinTarget(p)} data-testid={`admin-product-delete-${p.product_id}`}><Trash2 className="h-3 w-3" /></Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <Dialog open={!!editor} onOpenChange={() => setEditor(null)}>
                <DialogContent data-testid="admin-product-editor">
                    {editor && (
                        <>
                            <DialogHeader><DialogTitle className="font-display">{editor.product_id ? `Ürün Düzenle — ${editor.name}` : "Yeni Ürün Ekle"}</DialogTitle></DialogHeader>
                            <div className="space-y-3">
                                <Input value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} placeholder="Ürün adı" data-testid="admin-product-name" />
                                <SmartCategoryPicker categories={cats} value={editor.category_id} onChange={(v) => setEditor({ ...editor, category_id: v })} placeholder="Kategori seçin *" tid="admin-product-category" />
                                <div className="grid grid-cols-2 gap-3">
                                    <Input value={editor.unit} onChange={(e) => setEditor({ ...editor, unit: e.target.value })} placeholder="Birim (kg, lt, adet)" data-testid="admin-product-unit" />
                                    <select className="h-10 rounded-md border bg-background px-3 text-sm" value={editor.status} onChange={(e) => setEditor({ ...editor, status: e.target.value })} data-testid="admin-product-status">
                                        <option value="active">Aktif</option>
                                        <option value="passive">Pasif</option>
                                    </select>
                                    <select className="h-10 rounded-md border bg-background px-3 text-sm" value={editor.quality || ""} onChange={(e) => setEditor({ ...editor, quality: e.target.value })} data-testid="admin-product-quality">
                                        <option value="">Kalite: Belirtilmedi</option>
                                        {QUALITY_OPTIONS.map((qo) => <option key={qo} value={qo}>{qo}</option>)}
                                    </select>
                                </div>
                                <Input value={editor.description || ""} onChange={(e) => setEditor({ ...editor, description: e.target.value })} placeholder="Açıklama" data-testid="admin-product-desc" />
                                <div>
                                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ürün Görseli</p>
                                    <div className="flex items-center gap-3">
                                        {editor.image ? (
                                            <img src={editor.image} alt="" className="h-16 w-16 rounded-lg border object-cover" data-testid="admin-product-image-preview" />
                                        ) : (
                                            <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-muted"><Images className="h-6 w-6 text-muted-foreground" /></div>
                                        )}
                                        <div className="flex flex-col gap-1.5">
                                            <label className={`inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border px-3 text-xs font-medium hover:bg-muted ${imgBusy ? "opacity-60" : ""}`} data-testid="admin-product-image-upload">
                                                <input type="file" accept="image/*" className="hidden" disabled={imgBusy} onChange={(e) => { uploadEditorImage(e.target.files[0]); e.target.value = ""; }} data-testid="admin-product-image-input" />
                                                <Upload className="h-3.5 w-3.5" /> {imgBusy ? "Yükleniyor..." : "Yeni Yükle"}
                                            </label>
                                            <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={openMediaPicker} data-testid="admin-product-pick-media">
                                                <Images className="mr-1 h-3.5 w-3.5" /> Kütüphaneden Seç
                                            </Button>
                                        </div>
                                    </div>
                                    {sugMedia.length > 0 && (
                                        <div className="mt-2" data-testid="admin-product-suggestions">
                                            <p className="mb-1 text-[11px] font-medium text-muted-foreground">Önerilen görseller (ürün adına göre):</p>
                                            <div className="flex flex-wrap gap-2">
                                                {sugMedia.map((m) => (
                                                    <button key={m.id} type="button" onClick={() => setEditor({ ...editor, image: m.url })} className={`h-14 w-14 overflow-hidden rounded-lg border transition-shadow hover:ring-2 hover:ring-terra-500 ${editor.image === m.url ? "ring-2 ring-terra-600" : ""}`} data-testid={`admin-sug-${m.id}`} title={m.original_filename}>
                                                        <img src={m.url} alt="" className="h-full w-full object-cover" loading="lazy" />
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {mediaOpen && (
                                        <div className="mt-2 rounded-lg border p-2" data-testid="admin-product-media-picker">
                                            <Input value={mediaQ} onChange={(e) => { setMediaQ(e.target.value); fetchMedia(e.target.value); }} placeholder="Görsel adı ile ara..." className="mb-2 h-8 text-xs" data-testid="admin-product-media-search" />
                                            <div className="grid max-h-48 grid-cols-6 gap-2 overflow-y-auto">
                                                {(mediaItems || []).map((m) => (
                                                    <button key={m.id} type="button" onClick={() => { setEditor({ ...editor, image: m.url }); setMediaOpen(false); }} className="aspect-square overflow-hidden rounded-md border transition-shadow hover:ring-2 hover:ring-terra-500" data-testid={`media-pick-${m.id}`} title={m.original_filename}>
                                                        <img src={m.url} alt="" className="h-full w-full object-cover" loading="lazy" />
                                                    </button>
                                                ))}
                                                {mediaItems && !mediaItems.length && <p className="col-span-full p-4 text-center text-xs text-muted-foreground">Sonuç yok — farklı bir isim deneyin veya yeni görsel yükleyin.</p>}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setEditor(null)}>Vazgeç</Button>
                                <Button onClick={save} disabled={busy} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="admin-product-save">{busy ? "Kaydediliyor..." : "Kaydet"}</Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={exOpen} onOpenChange={setExOpen}>
                <DialogContent className="max-w-2xl" data-testid="admin-product-excel-dialog">
                    <DialogHeader><DialogTitle className="font-display">Excel ile Toplu Ürün Ekle</DialogTitle></DialogHeader>
                    {!exPreview ? (
                        <div className="space-y-3">
                            <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border border-dashed p-8 text-center transition-colors hover:border-terra-500/50" data-testid="admin-product-excel-drop">
                                <input type="file" accept=".xlsx" className="hidden" disabled={exBusy} onChange={(e) => uploadExcel(e.target.files[0])} data-testid="admin-product-excel-input" />
                                <Upload className="h-8 w-8 text-muted-foreground" />
                                <p className="text-sm font-semibold">{exBusy ? "Okunuyor ve doğrulanıyor..." : ".xlsx dosyası seçin"}</p>
                                <p className="text-xs text-muted-foreground">Sütunlar: Ürün Adı, Kategori, Birim, Fiyat, Stok, MOQ, Barkod, Teslim (gün), Kalite</p>
                            </label>
                            <a href={`${API}/excel/template?module=products`} className="text-xs font-medium text-primary hover:underline" data-testid="admin-product-excel-template">Şablonu indir</a>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <p className="text-sm"><span className="font-mono font-bold">{exPreview.import_id}</span> — {exPreview.total} satır: <span className="font-semibold text-emerald-700">{exPreview.ok} geçerli</span>{exPreview.failed > 0 && <span className="font-semibold text-red-600">, {exPreview.failed} hatalı</span>}</p>
                            <div className="max-h-64 overflow-y-auto rounded-lg border" data-testid="admin-product-excel-preview">
                                {exPreview.rows.map((r) => (
                                    <div key={r.satir} className="border-b px-3 py-2 text-xs last:border-0" data-testid={`excel-row-${r.satir}`}>
                                        <div className="flex justify-between">
                                            <span className="font-medium">{r.satir}. {r.urun} <span className={r.category_id ? "font-medium text-emerald-700" : "text-muted-foreground"}>({catLabel(cats, r)})</span></span>
                                            <Badge variant="outline" className={`text-[10px] ${r.durum === "ok" ? "border-emerald-200 bg-emerald-100 text-emerald-800" : "border-red-200 bg-red-100 text-red-700"}`}>{r.durum === "ok" ? "Geçerli" : "Hatalı"}</Badge>
                                        </div>
                                        {r.hatalar?.map((h, i) => <p key={i} className="text-red-600">{h.alan}: {h.neden}</p>)}
                                    </div>
                                ))}
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setExPreview(null)}>Geri</Button>
                                <Button onClick={commitExcel} disabled={exBusy || !exPreview.ok} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="admin-product-excel-commit">
                                    {exBusy ? "Uygulanıyor..." : `${exPreview.ok} Ürünü Kataloğa Ekle`}
                                </Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <CriticalPinDialog
                open={!!pinTarget}
                onClose={() => setPinTarget(null)}
                onSubmit={remove}
                actionLabel={pinTarget ? `"${pinTarget.name}" ürününü silme (tüm teklifleri kapatılacak)` : ""}
                busy={busy}
            />
        </div>
    );
}
